import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FolderOpen } from "lucide-react";
import { api, coverUrl } from "@/lib/api";
import { Reveal, ChapterBadge } from "@/components/Reveal";

export default function Portfolio() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/portfolio/categories").then((r) => r.data).catch(() => []),
      api.get("/portfolio/items", { params: { featured: true, limit: 6 } }).then((r) => r.data.items).catch(() => []),
    ]).then(([cats, items]) => {
      setCategories(cats);
      setFeatured(items);
      setLoaded(true);
    });
  }, []);

  const topCategories = [...categories].sort((a, b) => b.item_count - a.item_count).slice(0, 6);
  const hasWork = categories.some((c) => c.item_count > 0);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-20 sm:py-28 border-t border-charcoal-900/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ChapterBadge number="12" label="Selected Work" />
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal-900 leading-[1.1]">
              A body of work built across <em className="italic text-copper-600">14+ years.</em>
            </h2>
            <p className="mt-4 text-base text-charcoal-600 leading-relaxed">
              From social content and campaigns to presentations, advertising, corporate
              communication and video — a living archive of the formats and creative environments
              Sahil has worked across.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/portfolio"
              data-testid="explore-work-link"
              className="group inline-flex items-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-7 py-3.5 hover:bg-copper-600 transition-colors duration-300"
            >
              Explore the work
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        {loaded && !hasWork && (
          <Reveal className="mt-14">
            <div
              data-testid="portfolio-empty-state"
              className="rounded-2xl border border-dashed border-charcoal-900/20 bg-surface-white/60 p-12 text-center"
            >
              <FolderOpen className="w-8 h-8 mx-auto text-copper-600/60" />
              <p className="mt-4 font-serif text-2xl text-charcoal-900">Work will be added here</p>
              <p className="mt-2 text-sm text-charcoal-500 max-w-md mx-auto leading-relaxed">
                The archive is being curated. New categories and creative work appear here
                automatically as they are published.
              </p>
            </div>
          </Reveal>
        )}

        {topCategories.length > 0 && (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {topCategories.map((cat, i) => (
              <Reveal key={cat.id} delay={Math.min(i * 0.06, 0.3)}>
                <Link
                  to={`/portfolio/${cat.slug}`}
                  data-testid={`home-category-${cat.slug}`}
                  className="group block bg-surface-white border border-charcoal-900/10 rounded-2xl overflow-hidden hover:shadow-xl hover:border-copper-600/30 transition-[box-shadow,border-color] duration-300"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-surface-muted">
                    {cat.cover_path ? (
                      <img
                        src={coverUrl(cat)}
                        alt={cat.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center group-hover:scale-[1.03] transition-transform duration-500"
                        style={{ backgroundImage: "repeating-linear-gradient(135deg, rgba(18,19,22,0.035) 0 1px, transparent 1px 14px)" }}
                      >
                        <span className="font-serif italic text-6xl text-charcoal-900/15 select-none">{cat.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-lg font-semibold text-charcoal-900">{cat.name}</h3>
                      <span className="font-mono text-[10px] tracking-[0.15em] text-charcoal-500 shrink-0">
                        {cat.item_count} {cat.item_count === 1 ? "WORK" : "WORKS"}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-charcoal-600 leading-relaxed line-clamp-2">{cat.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-charcoal-500 group-hover:text-copper-600 transition-colors">
                      View Work <span aria-hidden="true">→</span>
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
